import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import { CustomImage } from 'contexts/CustomImagesContext/CustomImagesProvider';
import {
  getImageBlobUrlFromMatrix,
  getStoredImagesFromMatrix,
  MatrixCustomImage,
} from 'modules/MediaDevicesSettings/VisualEffectsSettings/utils/matrixImageStorage';
import logger from 'utils/logging/faro';

const SELECTED_CUSTOM_IMAGE_ID_KEY = 'meedio_connect_selected_custom_image_id';

/**
 * Revoke a single blob URL if it's a valid blob URL
 */
export const revokeBlobUrl = (blobUrl: string): void => {
  if (blobUrl && blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
};

/**
 * Clean up old blob URLs that are no longer in use
 */
export const cleanupUnusedBlobUrls = (
  blobUrlsRef: React.MutableRefObject<Map<string, string>>,
  currentMxcUris: Set<string>
): void => {
  const urlsToRevoke: string[] = [];

  blobUrlsRef.current.forEach((blobUrl, mxcUri) => {
    if (currentMxcUris.has(mxcUri)) return;

    urlsToRevoke.push(blobUrl);
    blobUrlsRef.current.delete(mxcUri);
  });

  urlsToRevoke.forEach(revokeBlobUrl);
};

/**
 * Convert a Matrix image to CustomImage format with blob URL
 * Reuses existing blob URLs when possible to avoid recreating them
 */
export const convertMatrixImageToCustomImage = async (
  matrixClient: MatrixClient,
  blobUrlsRef: React.MutableRefObject<Map<string, string>>,
  { id, name, mxcUri, hash, dimensions }: MatrixCustomImage
): Promise<CustomImage> => {
  let blobUrl = blobUrlsRef.current.get(mxcUri);

  // Only create new blob URL if we don't have one cached
  if (!blobUrl) {
    const fetchedBlobUrl = await getImageBlobUrlFromMatrix(matrixClient, mxcUri);
    if (fetchedBlobUrl) {
      blobUrl = fetchedBlobUrl;
      blobUrlsRef.current.set(mxcUri, blobUrl);
    }
  }

  return {
    id,
    name,
    mxcUri,
    hash,
    dimensions,
    path: blobUrl || '',
  };
};

/**
 * Load and convert all Matrix images to CustomImage format
 */
export const loadCustomImagesFromMatrix = async (
  matrixClient: MatrixClient,
  blobUrlsRef: React.MutableRefObject<Map<string, string>>,
  cleanupUnusedBlobUrlsFn: (currentMxcUris: Set<string>) => void
): Promise<CustomImage[]> => {
  try {
    const matrixImages = getStoredImagesFromMatrix(matrixClient);
    const currentMxcUris = new Set(matrixImages.map(({ mxcUri }) => mxcUri));

    // Clean up blob URLs for images that are no longer in the list
    cleanupUnusedBlobUrlsFn(currentMxcUris);

    // Convert Matrix images to CustomImage format with blob URLs
    const convertedImagesPromises = matrixImages.map((matrixImage) =>
      convertMatrixImageToCustomImage(matrixClient, blobUrlsRef, matrixImage)
    );

    return await Promise.all(convertedImagesPromises);
  } catch (error) {
    logger.error('Error loading custom images from Matrix', error);
    return [];
  }
};

/**
 * Get selected custom image ID from localStorage
 */
export const getSelectedImageIdFromStorage = (): string | null => {
  try {
    const storedId = window.localStorage.getItem(SELECTED_CUSTOM_IMAGE_ID_KEY);
    if (!storedId) return null;

    const parsedId = JSON.parse(storedId);
    return parsedId || null;
  } catch (error) {
    logger.warn('Error reading selected custom image ID from storage', error);
    return null;
  }
};

/**
 * Find a custom image by ID in the images array
 */
export const findCustomImageById = (images: CustomImage[], id: string): CustomImage | undefined =>
  images.find((img) => img.id === id);

/**
 * Fetch a custom image path by ID from Matrix if not found in state
 */
export const fetchCustomImagePathFromMatrix = async (
  matrixClient: MatrixClient,
  id: string,
  blobUrlsRef: React.MutableRefObject<Map<string, string>>
): Promise<string | null> => {
  try {
    const matrixImages = getStoredImagesFromMatrix(matrixClient);
    const matrixImage = matrixImages.find((img) => img.id === id);
    if (!matrixImage) return null;

    const { mxcUri } = matrixImage;

    // Fetch and create blob URL
    const blobUrl = await getImageBlobUrlFromMatrix(matrixClient, mxcUri);
    if (!blobUrl) return null;

    // Cache the blob URL
    blobUrlsRef.current.set(mxcUri, blobUrl);

    return blobUrl;
  } catch (error) {
    logger.warn('Error fetching image from Matrix', error);
    return null;
  }
};

/**
 * Create a CustomImage object from Matrix image data
 */
export const createCustomImageFromMatrix = (matrixImage: MatrixCustomImage, blobUrl: string): CustomImage => {
  const { id, name, mxcUri, hash, dimensions } = matrixImage;
  return {
    id,
    name,
    mxcUri,
    hash,
    dimensions,
    path: blobUrl,
  };
};

export { SELECTED_CUSTOM_IMAGE_ID_KEY };
